import React, { useRef, useEffect, useState } from "react";

const InfiniteSlider = ({ children, speed = 50 /* px/s */ }) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [copies, setCopies] = useState(2);

  // 1) Determine quantas cópias precisamos
  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track) return;
    const fullW = track.scrollWidth;
    const contW = container.getBoundingClientRect().width;
    const originalW = fullW / copies;
    const need = Math.max(2, Math.ceil((contW + originalW) / originalW));
    setCopies(need);
  }, [children, copies]);

  // 2) Scroll contínuo + drag com inércia + suporte a toque
  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track) return;

    let pos = 0;
    let paused = false;
    let isDragging = false;
    let frameId = 0;
    let lastTime = performance.now();

    let startX = 0,
      startY = 0,
      prevPos = 0;
    let lastX = 0,
      lastT = 0;
    let velocity = 0;

    let isTouching = false;

    const friction = 0.92;
    const threshold = 0.5;
    const touchDirectionThreshold = 8;
    const touchStartOptions = { passive: true };
    const touchMoveOptions = { passive: false };

    const removeTouchListeners = () => {
      document.removeEventListener("touchmove", onTouchMove, touchMoveOptions);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };

    const step = (t) => {
      const dt = (t - lastTime) / 1000;
      lastTime = t;
      if (!paused && !isDragging) {
        pos = (pos + speed * dt) % (track.scrollWidth / copies);
        track.style.transform = `translateX(-${pos}px)`;
      }
      frameId = requestAnimationFrame(step);
    };

    frameId = requestAnimationFrame(step);

    // ✅ FIX: Iniciar no meio para permitir scroll em ambas as direções
    requestAnimationFrame(() => {
      pos = track.scrollWidth / copies / 2;
      track.style.transform = `translateX(-${pos}px)`;
    });

    const enter = () => {
      paused = true;
    };
    const leave = () => {
      paused = false;
    };
    container.addEventListener("mouseenter", enter);
    container.addEventListener("mouseleave", leave);

    const onMouseDown = (e) => {
      isDragging = true;
      startX = e.clientX;
      prevPos = pos;
      lastX = startX;
      lastT = performance.now();
      paused = true;
      container.style.cursor = "grabbing";
      track.style.transform = `translateX(-${pos}px)`;
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;
      const now = performance.now();
      const dx = e.clientX - lastX;
      const dt = (now - lastT) / 1000;
      if (dt > 0) {
        velocity = dx / dt;
      }
      lastX = e.clientX;
      lastT = now;
      const delta = e.clientX - startX;
      pos = prevPos - delta;
      track.style.transform = `translateX(-${pos}px)`;
    };

    const onMouseUp = () => {
      isDragging = false;
      container.style.cursor = "grab";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      let momentum = -velocity / 60;
      if (!isFinite(momentum)) momentum = 0;
      const inertia = () => {
        momentum *= friction;
        pos += momentum;
        track.style.transform = `translateX(-${pos}px)`;
        if (Math.abs(momentum) > threshold) {
          frameId = requestAnimationFrame(inertia);
        } else {
          paused = false;
        }
      };
      inertia();
    };

    // === Mobile touch support ===
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      isTouching = true;
      isDragging = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      prevPos = pos;
      lastX = startX;
      lastT = performance.now();
      velocity = 0;
      track.style.transition = "none";
      document.addEventListener("touchmove", onTouchMove, touchMoveOptions);
      document.addEventListener("touchend", onTouchEnd);
      document.addEventListener("touchcancel", onTouchEnd);
    };

    const onTouchMove = (e) => {
      if (!isTouching || e.touches.length !== 1) return;
      const now = performance.now();
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const totalX = x - startX;
      const totalY = y - startY;

      if (!isDragging) {
        const absX = Math.abs(totalX);
        const absY = Math.abs(totalY);

        if (Math.max(absX, absY) < touchDirectionThreshold) return;

        if (absY >= absX) {
          isTouching = false;
          paused = false;
          removeTouchListeners();
          return;
        }

        isDragging = true;
        paused = true;
        prevPos = pos;
        lastX = x;
        lastT = now;
      }

      e.preventDefault();

      const dx = x - lastX;
      const dt = (now - lastT) / 1000;
      if (dt > 0) {
        velocity = dx / dt;
      }
      lastX = x;
      lastT = now;
      const delta = x - startX;
      pos = prevPos - delta;
      track.style.transform = `translateX(-${pos}px)`;
    };

    const onTouchEnd = () => {
      isTouching = false;
      removeTouchListeners();

      if (!isDragging) {
        paused = false;
        return;
      }

      isDragging = false;
      let momentum = -velocity / 60;
      if (!isFinite(momentum)) momentum = 0;
      const inertia = () => {
        momentum *= friction;
        pos += momentum;
        track.style.transform = `translateX(-${pos}px)`;
        if (Math.abs(momentum) > threshold) {
          frameId = requestAnimationFrame(inertia);
        } else {
          paused = false;
        }
      };
      inertia();
    };

    container.addEventListener("mousedown", onMouseDown);
    container.addEventListener("touchstart", onTouchStart, touchStartOptions);

    return () => {
      cancelAnimationFrame(frameId);
      container.removeEventListener("mouseenter", enter);
      container.removeEventListener("mouseleave", leave);
      container.removeEventListener("mousedown", onMouseDown);
      container.removeEventListener("touchstart", onTouchStart, touchStartOptions);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      removeTouchListeners();
    };
  }, [copies, speed]);

  // 3) Renderiza itens duplicados
  const items = React.Children.toArray(children);
  const rendered = [];
  for (let i = 0; i < copies; i++) {
    items.forEach((child, idx) => {
      rendered.push(
        <div
          key={`${i}-${idx}`}
          style={{
            display: "inline-block",
            marginRight: "16px",
            verticalAlign: "top",
          }}>
          {child}
        </div>
      );
    });
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        position: "relative",
        touchAction: "pan-y",
      }}>
      <div
        ref={trackRef}
        style={{
          whiteSpace: "nowrap",
          transform: "translateX(0)",
        }}>
        {rendered}
      </div>
    </div>
  );
};

export default InfiniteSlider;
